import React, { useState } from 'react';
import styled from 'styled-components';
import Select from 'components/FormFields/Select';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const PageDescription = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 48px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin: 0 0 24px 0;
`;

const DemoRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  > div {
    flex: 1;
    min-width: 280px;
  }
`;

const SelectedValue = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB'};
  border-radius: 8px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};

  code {
    color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  }
`;

const SelectDocs = () => {
  const [basicValue, setBasicValue] = useState('');
  const [statusValue, setStatusValue] = useState('active');
  const [priorityValue, setPriorityValue] = useState('');

  const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  const priorityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
    { label: 'Critical', value: 'critical' },
  ];

  const categoryOptions = [
    { label: 'Technology', value: 'tech' },
    { label: 'Finance', value: 'finance' },
    { label: 'Healthcare', value: 'healthcare' },
    { label: 'Education', value: 'education' },
  ];

  const props = [
    {
      name: 'label',
      type: 'string',
      required: false,
      description: 'Label text displayed above the select',
    },
    {
      name: 'required',
      type: 'boolean',
      required: false,
      description: 'Shows a red asterisk next to the label (default: false)',
    },
    {
      name: 'value',
      type: 'string | number',
      required: false,
      description: 'The currently selected value',
    },
    {
      name: 'onChange',
      type: 'function',
      required: true,
      description: 'Callback when value changes: (event) => void',
    },
    {
      name: 'placeholder',
      type: 'string',
      required: false,
      description: 'Placeholder option text (shown as disabled first option)',
    },
    {
      name: 'options',
      type: 'array',
      required: false,
      description:
        'Array of option objects with { label: string, value: string | number }',
    },
    {
      name: 'helperText',
      type: 'string',
      required: false,
      description: 'Helper or error text displayed below the select',
    },
    {
      name: 'error',
      type: 'boolean',
      required: false,
      description: 'Applies error styling (default: false)',
    },
    {
      name: 'width',
      type: 'string',
      required: false,
      description: 'Width of the select (default: "100%")',
    },
    {
      name: 'maxWidth',
      type: 'string',
      required: false,
      description: 'Maximum width of the select',
    },
    {
      name: 'disabled',
      type: 'boolean',
      required: false,
      description: 'Disables the select (default: false)',
    },
  ];

  return (
    <>
      <PageTitle>Select</PageTitle>
      <PageDescription>
        A native HTML select dropdown component with consistent styling and
        theme support. Use for simple selection scenarios where users need to
        choose from a predefined list of options.
      </PageDescription>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable props={props} />
      </Section>

      <Section>
        <SectionTitle>Basic Select</SectionTitle>
        <ComponentDemo
          title="Simple select with placeholder"
          code={`import Select from 'components/FormFields/Select';

const [value, setValue] = useState('');

const options = [
  { label: 'Option One', value: 'option-1' },
  { label: 'Option Two', value: 'option-2' },
  { label: 'Option Three', value: 'option-3' },
];

<Select
  label="Category"
  value={value}
  onChange={(e) => setValue(e.target.value)}
  options={options}
  placeholder="Select a category..."
/>`}
        >
          <DemoRow>
            <div>
              <Select
                label="Category"
                value={basicValue}
                onChange={(e) => setBasicValue(e.target.value)}
                options={categoryOptions}
                placeholder="Select a category..."
              />
              {basicValue && (
                <SelectedValue>
                  Selected: <code>{basicValue}</code>
                </SelectedValue>
              )}
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Status Select</SectionTitle>
        <ComponentDemo
          title="Select for status fields (common pattern)"
          code={`const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

<Select
  label="Status"
  value={status}
  onChange={(e) => setStatus(e.target.value)}
  options={statusOptions}
/>`}
        >
          <DemoRow>
            <div>
              <Select
                label="Status"
                value={statusValue}
                onChange={(e) => setStatusValue(e.target.value)}
                options={statusOptions}
              />
              <SelectedValue>
                Selected: <code>{statusValue}</code>
              </SelectedValue>
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Required Field</SectionTitle>
        <ComponentDemo
          title="Select with required indicator"
          code={`<Select
  label="Priority"
  required
  value={value}
  onChange={(e) => setValue(e.target.value)}
  options={priorityOptions}
  placeholder="Select priority..."
/>`}
        >
          <DemoRow>
            <div>
              <Select
                label="Priority"
                required
                value={priorityValue}
                onChange={(e) => setPriorityValue(e.target.value)}
                options={priorityOptions}
                placeholder="Select priority..."
              />
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>With Error</SectionTitle>
        <ComponentDemo
          title="Select with error state"
          code={`<Select
  label="Category"
  value=""
  onChange={handleChange}
  options={options}
  placeholder="Select a category..."
  error
  helperText="This field is required"
/>`}
        >
          <DemoRow>
            <div>
              <Select
                label="Category"
                value=""
                onChange={(e) => setPriorityValue(e.target.value)}
                options={categoryOptions}
                placeholder="Select a category..."
                error
                helperText="This field is required"
              />
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Disabled State</SectionTitle>
        <ComponentDemo
          title="Disabled select"
          code={`<Select
  label="Status"
  value="active"
  onChange={handleChange}
  options={statusOptions}
  disabled
/>`}
        >
          <DemoRow>
            <div>
              <Select
                label="Status"
                value="active"
                options={statusOptions}
                disabled
              />
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>With Helper Text</SectionTitle>
        <ComponentDemo
          title="Select with helper text"
          code={`<Select
  label="Priority Level"
  value={value}
  onChange={(e) => setValue(e.target.value)}
  options={priorityOptions}
  helperText="Higher priority items will be processed first"
/>`}
        >
          <DemoRow>
            <div>
              <Select
                label="Priority Level"
                value="medium"
                onChange={undefined}
                options={priorityOptions}
                helperText="Higher priority items will be processed first"
              />
            </div>
          </DemoRow>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Import</SectionTitle>
        <CodeBlock
          language="javascript"
          code={`import Select from 'components/FormFields/Select';`}
        />
      </Section>

      <Section>
        <SectionTitle>When to Use</SectionTitle>
        {/* eslint-disable jsx-a11y/accessible-emoji */}
        <ul style={{ lineHeight: 1.8 }}>
          <li>
            <strong>Select</strong> - Use for simple, predefined options
            (status, priority, categories)
          </li>
          <li>
            <strong>CreatableSelect</strong> - Use when users need to add new
            options that don&apos;t exist yet
          </li>
          <li>
            <strong>AutoSuggest</strong> - Use for large datasets that need
            server-side filtering
          </li>
        </ul>
        {/* eslint-enable jsx-a11y/accessible-emoji */}
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        {/* eslint-disable jsx-a11y/accessible-emoji */}
        <ul style={{ lineHeight: 1.8 }}>
          <li>🎨 Fully theme-aware (light/dark mode support)</li>
          <li>✓ Native HTML select for accessibility</li>
          <li>🏷️ Optional label with required indicator</li>
          <li>❌ Error state with helper text</li>
          <li>📏 Customizable width and max-width</li>
          <li>🚫 Disabled state support</li>
        </ul>
        {/* eslint-enable jsx-a11y/accessible-emoji */}
      </Section>
    </>
  );
};

export default SelectDocs;
